import type { InventoryLineRow, InventorySkuRow, InventoryStoreRow, LineDetail } from './types';

/**
 * Cache-backed inventory fetchers. Each returns the array straight from the
 * Go cache (no daemon round-trip; bi_export computes nightly). 503 means
 * "bi_export hasn't produced the file yet" — surface as a friendly message.
 */

const FETCH_TIMEOUT_MS = 30_000;

function isMock(): boolean {
  return import.meta.env.VITE_USE_MOCKS === '1';
}

async function getJSON<T>(url: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    if (res.status === 503) {
      throw new Error('Inventario en preparación — el primer cron de bi_export aún no produce los archivos.');
    }
    if (!res.ok) {
      throw new Error(`${url} failed: ${res.status}`);
    }
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`${url} timed out after ${FETCH_TIMEOUT_MS}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchInventorySkus(): Promise<InventorySkuRow[]> {
  if (isMock()) return [];
  return getJSON<InventorySkuRow[]>('/api/inventory/skus');
}

export async function fetchInventoryLines(): Promise<InventoryLineRow[]> {
  if (isMock()) return [];
  return getJSON<InventoryLineRow[]>('/api/inventory/lines');
}

export async function fetchInventoryStores(): Promise<InventoryStoreRow[]> {
  if (isMock()) return [];
  return getJSON<InventoryStoreRow[]>('/api/inventory/stores');
}

/**
 * v2.57 — on-demand per-line drill-down (LineDetailModal). Hits the live daemon
 * via /api/line-detail (not the nightly snapshot), so it's a couple seconds at
 * worst. sid omitted/0 = all real stores. The Go side writes the daemon's inner
 * `data` object directly (no {ok,data} envelope) and strips value fields when
 * the user lacks cost:visible.
 */
export async function fetchLineDetail(line: string, sid?: number | null): Promise<LineDetail> {
  if (isMock()) return { line, description: '', total_parts: 0, suppliers: [], pcodes: [] };
  const params = new URLSearchParams({ line });
  if (sid != null && sid > 0) params.set('sid', String(sid));
  return getJSON<LineDetail>(`/api/line-detail?${params.toString()}`);
}
